import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "ai-ml",
  name: "AI・機械学習",
  nameEn: "AI & Machine Learning",
  seo: {
    title:
      "AI・機械学習を学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "AI・機械学習はChatGPT時代の必須スキル。Python + TensorFlow/PyTorchからLLMプロンプトエンジニアリングまで、ゆめさくエンジニアで実践的に学びましょう。",
    url: "/columns/ai-ml-programming-school",
  },
  hero: {
    title: "AI・機械学習を学べる女性向けプログラミングスクール",
    englishTitle: "Learn AI & Machine Learning",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "AI・機械学習" },
  ],
  overview: {
    heading: "AI・機械学習とは？",
    paragraphs: [
      "AI（人工知能）とは、人間の知的な振る舞いをコンピュータで実現する技術の総称です。機械学習はAIの中核をなす手法で、大量のデータからパターンを自動的に学習し、予測や分類を行います。",
      "ChatGPTに代表される大規模言語モデル（LLM）の登場により、AI技術は一気に身近な存在になりました。画像認識、自然言語処理、音声認識、レコメンドシステムなど、私たちの生活やビジネスのあらゆる場面でAI技術が活用されています。",
      "PythonとTensorFlow・PyTorchなどのフレームワークを使って機械学習モデルを構築するスキルに加え、LLMのプロンプトエンジニアリングやファインチューニングなどの新しいスキルも注目されています。",
    ],
    useCases: [
      {
        label: "画像認識・物体検出",
        description:
          "写真から顔を検出する、製品の不良品を自動判別する、医療画像を診断支援するなど、視覚情報をAIで処理します。",
      },
      {
        label: "自然言語処理（NLP）",
        description:
          "テキストの感情分析、自動翻訳、チャットボット、文書要約など、言語データをAIで理解・生成する技術です。",
      },
      {
        label: "レコメンドシステム",
        description:
          "ECサイトの「おすすめ商品」やNetflixの「あなたへのおすすめ」など、ユーザーの行動データから最適な提案を行います。",
      },
      {
        label: "LLMプロンプトエンジニアリング",
        description:
          "ChatGPTなどのLLMを業務に活用するためのプロンプト設計や、API連携によるAIアプリケーション開発を行います。",
      },
    ],
  },
  whyLearn: {
    heading: "AI・機械学習を学ぶべき3つの理由",
    items: [
      {
        title: "急速に拡大する市場と圧倒的な人材不足",
        description:
          "AI市場は年率30%以上で成長しており、2030年までに数十万人規模のAI人材が不足すると予測されています。今から学び始めることで、需要の高い希少人材として高い市場価値を得られます。",
      },
      {
        title: "ChatGPT時代にAIを「使う側」から「作る側」へ",
        description:
          "ChatGPTの登場でAIは身近になりましたが、AIを活用したサービスを設計・構築できるエンジニアはまだ少数です。AI技術の仕組みを理解して開発できるスキルは、今後あらゆる業界で求められます。",
      },
      {
        title: "高い年収水準と多様なキャリアパス",
        description:
          "AIエンジニア・データサイエンティストは、IT業界の中でも特に高い年収水準を誇ります。研究職、プロダクト開発、コンサルティングなど、多様なキャリアの選択肢があるのも魅力です。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでAI・機械学習を学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、Pythonの基礎を学んだ後、データ分析・機械学習の実践的なカリキュラムに進みます。数式の暗記ではなく、実際のデータセットを使って「手を動かしながら理解する」アプローチで学びます。",
      "ChatGPT APIを活用したアプリケーション開発や、プロンプトエンジニアリングの基礎も含まれており、最新のAI技術トレンドにも対応しています。",
    ],
    topics: [
      "Pythonによるデータ前処理（pandas・NumPy）",
      "データ可視化（matplotlib・seaborn）",
      "教師あり学習（回帰・分類）の基礎",
      "scikit-learnによる機械学習モデル構築",
      "ニューラルネットワークの基礎",
      "LLM API連携・プロンプトエンジニアリング入門",
      "実践プロジェクト：AIを活用したWebアプリ開発",
    ],
  },
  careerPaths: {
    heading: "AI・機械学習を活かせるキャリア",
    items: [
      {
        title: "AIエンジニア",
        description:
          "機械学習モデルの設計・開発・本番環境へのデプロイを担当します。画像認識、自然言語処理、レコメンドシステムなど、AI技術を実際のプロダクトに組み込む役割です。",
        salary: "600〜1,200万円",
      },
      {
        title: "データサイエンティスト",
        description:
          "ビジネスの課題をデータ分析と機械学習で解決する職種です。統計学・機械学習の知識とビジネス理解を兼ね備え、データに基づいた意思決定を推進します。",
        salary: "500〜900万円",
      },
      {
        title: "MLOpsエンジニア",
        description:
          "機械学習モデルの運用・監視・自動化を専門とする職種です。モデルのパフォーマンス維持、データパイプラインの構築、デプロイの自動化など、AIシステムの安定稼働を支えます。",
        salary: "600〜1,000万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "12〜18ヶ月（数学基礎+Python+ML）",
        luagate: "5〜6ヶ月（必要な数学も合わせて効率的に学習）",
      },
      {
        label: "挫折率",
        selfStudy: "数学・統計の壁で9割以上が挫折",
        luagate: "実践ファーストで数学は必要な分だけ段階的に導入",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "Kaggleのチュートリアルで止まりがち",
        luagate: "実際のビジネス課題を想定したAIプロジェクト",
      },
      {
        label: "質問対応",
        selfStudy: "モデルの精度が上がらない原因がわからない",
        luagate: "経験豊富なメンターがデータ選定から指導",
      },
      {
        label: "就職サポート",
        selfStudy: "AI分野の未経験求人の探し方がわからない",
        luagate: "AI企業とのネットワークを活かした就職支援",
      },
    ],
  },
  faq: [
    {
      question: "AI・機械学習を学ぶのに高い数学力は必要ですか？",
      answer:
        "高校数学レベル（行列・微分・確率）の基礎があると有利ですが、必須ではありません。ゆめさくエンジニアでは必要な数学を学習の中で段階的に導入するため、文系出身の方でも無理なく学べます。まずは手を動かしてコードを書くことから始めましょう。",
    },
    {
      question: "AIエンジニアになるには大学院卒が必要ですか？",
      answer:
        "研究職には大学院卒が求められることがありますが、AIを活用したプロダクト開発やMLOps、プロンプトエンジニアリングなどの実務ポジションでは、実践的なスキルと経験があれば学歴は必須ではありません。",
    },
    {
      question: "ChatGPTの登場でAIエンジニアの需要は減りますか？",
      answer:
        "むしろ増加しています。ChatGPTのようなLLMを自社サービスに組み込む、ファインチューニングする、プロンプトを最適化するなど、AI技術を実装できるエンジニアの需要はChatGPTの登場でさらに高まっています。",
    },
    {
      question: "Pythonが苦手でもAI・機械学習を学べますか？",
      answer:
        "AI・機械学習ではPythonが主要言語として使われるため、Pythonの基礎は必要です。ゆめさくエンジニアではPythonの基礎から段階的に学べるカリキュラムを提供しているので、Python未経験の方も安心して始められます。",
    },
    {
      question: "AI・機械学習の学習にハイスペックなPCは必要ですか？",
      answer:
        "学習段階ではGoogle ColabなどのクラウドサービスでGPUを無料で利用できるため、一般的なPCで問題ありません。ゆめさくエンジニアでもクラウド環境を活用した学習方法を指導しています。",
    },
  ],
  relatedLanguages: [
    { slug: "python", name: "Python" },
    { slug: "sql", name: "SQL" },
    { slug: "javascript", name: "JavaScript" },
    { slug: "git", name: "Git" },
  ],
};
